import { memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import Option from './Option';
import DropDownSelectOptions from '@/components/component-library/DropDownSelect/DropDownSelectOptions';
import CaretIcon from '@/components/component-library/Icon/types/Caret';
import { classes } from '@/helpers/styling';
import useFocusAndBlur from '@/hooks/browser/useFocusAndBlur';

const SelectorListVisible = ({
  options,
  moreOptions,
  selectedId,
  shouldDisplayMoreOption,
  indexToDisplayMoreOption,
  moreOptionExpanded,
  moreOptionHighlighted,
  onClickOption,
  onClickMore,
  className,
  optionClassName,
  listRef,
  onCloseMore,
  loading,
  OptionComponent,
  dropDownProps
}) => {
  const handleClickMoreOption = (...args) => {
    onClickMore();
    onClickOption(...args);
  };

  const {
    ref,
    onBlur
  } = useFocusAndBlur({
    blurCallback: onCloseMore,
    shouldFocus: moreOptionExpanded
  });

  const moreOption = moreOptions.find(({ id: moreOptionId }) => moreOptionId === selectedId);
  const moreDropDownValue = (moreOption || {}).value;
  const moreDropDownId = (moreOption || {}).id;

  const moreOptionsWithoutSelected = useMemo(() => moreOptions.filter(({ id }) => selectedId !== id), [selectedId, moreOptions]);

  return (
    <ul
      className={ classes('selector-h__list-visible', className, {
        '--is-loading': loading,
        '--has-loaded': !loading
      }) }
      ref={ listRef }
    >
      {options.map(({
        id: optionId, value, href
      }) => {
        const displayMoreOptionHere = (
          shouldDisplayMoreOption
          && (optionId === options[indexToDisplayMoreOption].id)
        );

        return (
          <li
            className={ classes('selector-h__option-wrapper', optionClassName) }
            key={ optionId }
          >
            {displayMoreOptionHere ? (
              <Option
                className='selector-h__more-option'
                id='more-option'
                onClick={ onClickMore }
                selected={ !!moreOption }
              >
                <div
                  className='selector-h__more-drop-down'
                >
                  <div
                    className={ classes('selector-h__more-text', {
                      '--is-open': moreOptionExpanded,
                      '--is-selected': moreOptionHighlighted
                    }) }
                    onBlur={ onBlur }
                    ref={ ref }
                    tabIndex={ 0 }
                  >
                    {OptionComponent ? (
                      <OptionComponent
                        id={ moreDropDownId || 'more' }
                        isMoreElement
                        selected={ moreOptionHighlighted }
                        value={ moreDropDownValue || 'More' }
                      />
                    ) : (
                      <>
                        More
                        <CaretIcon />
                      </>
                    )}
                    <div className={ classes('selector-h__more-drop-down-contents', { '--is-open': moreOptionExpanded }) }>
                      <DropDownSelectOptions
                        onClickOption={ handleClickMoreOption }
                        onClose={ onCloseMore }
                        open={ moreOptionExpanded }
                        options={ moreOptionsWithoutSelected }
                        selectedId={ selectedId }
                        { ...dropDownProps }
                      />
                    </div>
                  </div>
                </div>
              </Option>
            ) : (
              <Option
                href={ href }
                id={ optionId }
                onClick={ () => onClickOption({
                  id: optionId,
                  value
                }) }
                OptionComponent={ OptionComponent }
                selected={ optionId=== selectedId }
                value={ value }
              />
            )}
          </li>
        );
      })}
    </ul>
  );
};

SelectorListVisible.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired
    })
  ),
  moreOptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired
    })
  ),
  selectedId: PropTypes.string,
  shouldDisplayMoreOption: PropTypes.bool,
  indexToDisplayMoreOption: PropTypes.number,
  moreOptionExpanded: PropTypes.bool,
  moreOptionHighlighted: PropTypes.bool,
  onClickOption: PropTypes.func,
  onClickMore: PropTypes.func,
  className: PropTypes.string,
  optionClassName: PropTypes.string,
  listRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.object
  ]),
  onCloseMore: PropTypes.func,
  loading: PropTypes.bool,
  OptionComponent: PropTypes.element,
  dropDownProps: PropTypes.object
};

SelectorListVisible.defaultProps = {
  options: [],
  moreOptions: [],
  selectedId: undefined,
  shouldDisplayMoreOption: false,
  indexToDisplayMoreOption: undefined,
  moreOptionExpanded: false,
  moreOptionHighlighted: false,
  onClickOption: undefined,
  onClickMore: undefined,
  className: '',
  optionClassName: '',
  listRef: null,
  onCloseMore: undefined,
  loading: false,
  OptionComponent: undefined,
  dropDownProps: {}
};

export default memo(SelectorListVisible);
